import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CalendarClock } from 'lucide-react';
import api from '@/lib/api';
import { formatDateOnly } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/toast';
import { useAuthStore } from '@/stores/auth.store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { StatCard } from '@/components/ui/stat-card';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

/** One received lot of an expiry-tracked product (GET /inventory/expiring). */
interface ExpiringBatch {
  id: string;
  productId: string;
  batchNo: string | null;
  quantity: number;
  expiryDate: string | null;
  daysUntilExpiry: number | null;
  product: { id: string; name: string; sku?: string | null };
}

interface ExpiringResponse {
  summary: { expired: number; within7Days: number; within30Days: number; within60Days: number; total: number };
  batches: ExpiringBatch[];
}

const HORIZON_DAYS = 60;

export function ExpiryTrackingTab() {
  const { t } = useTranslation('inventory');
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  const canWriteOff = hasPermission('inventory:update') || hasPermission('inventory:*');
  const [writeOff, setWriteOff] = useState<ExpiringBatch | null>(null);

  const { data, isLoading } = useQuery<ExpiringResponse>({
    queryKey: ['expiring-batches', HORIZON_DAYS],
    queryFn: () => api.get<ExpiringResponse>(`/inventory/expiring?days=${HORIZON_DAYS}`).then((r) => r.data),
    staleTime: 60_000,
  });

  const writeOffMutation = useMutation({
    mutationFn: (batch: ExpiringBatch) =>
      api.post('/inventory/adjust', {
        productId: batch.productId,
        type: 'DAMAGE',
        quantity: batch.quantity,
        batchId: batch.id,
        reason: t('expiryWriteOffReason', { batch: batch.batchNo ?? t('expiryNoBatchNo') }),
      }),
    onSuccess: () => {
      toast.success(t('expiryWrittenOff'));
      setWriteOff(null);
      queryClient.invalidateQueries({ queryKey: ['expiring-batches'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['stock-movements'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['products-pos'] });
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, t('expiryWriteOffFailed'))),
  });

  const rows = data?.batches ?? [];
  const summary = data?.summary;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('expiryTrackingHint', { days: HORIZON_DAYS })}</p>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label={t('expiryExpired')} value={summary?.expired ?? 0} format="number" accent="danger" />
        <StatCard label={t('expiryWithin7')} value={summary?.within7Days ?? 0} format="number" accent="warning" />
        <StatCard label={t('expiryWithin30')} value={summary?.within30Days ?? 0} format="number" accent="warning" />
        <StatCard label={t('expiryWithin60')} value={summary?.within60Days ?? 0} format="number" accent="info" />
      </div>

      {isLoading ? (
        <SkeletonTable rows={6} cols={6} />
      ) : rows.length === 0 ? (
        <EmptyState icon={CalendarClock} title={t('expiryNoneTitle')} description={t('expiryNoneDesc')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('expiryColProduct')}</Th>
              <Th>{t('expiryColBatch')}</Th>
              <Th className="text-end">{t('expiryColQty')}</Th>
              <Th>{t('expiryColExpiry')}</Th>
              <Th>{t('expiryColStatus')}</Th>
              {canWriteOff && <Th className="text-end">{t('expiryColActions')}</Th>}
            </Tr>
          </THead>
          <TBody>
            {rows.map((b) => {
              const expired = b.daysUntilExpiry !== null && b.daysUntilExpiry <= 0;
              const days = b.daysUntilExpiry ?? 0;
              return (
                <Tr key={b.id}>
                  <Td label={t('expiryColProduct')}>
                    <div className="font-medium">{b.product.name}</div>
                    {b.product.sku && <div className="text-xs text-muted-foreground">SKU: {b.product.sku}</div>}
                  </Td>
                  <Td label={t('expiryColBatch')}>{b.batchNo ?? <span className="text-muted-foreground">{t('expiryNoBatchNo')}</span>}</Td>
                  <Td className="text-end" label={t('expiryColQty')}>{b.quantity}</Td>
                  <Td label={t('expiryColExpiry')}>{b.expiryDate ? formatDateOnly(b.expiryDate) : '—'}</Td>
                  <Td label={t('expiryColStatus')}>
                    {expired ? (
                      <Badge variant="danger">{t('expiryStatusExpired')}</Badge>
                    ) : (
                      <Badge variant={days <= 7 ? 'danger' : days <= 30 ? 'warning' : 'info'}>{t('expiryDaysLeft', { days })}</Badge>
                    )}
                  </Td>
                  {canWriteOff && (
                    <Td className="text-end" label={t('expiryColActions')}>
                      {expired && (
                        <Button size="sm" variant="outline" onClick={() => setWriteOff(b)}>{t('expiryWriteOff')}</Button>
                      )}
                    </Td>
                  )}
                </Tr>
              );
            })}
          </TBody>
        </Table>
      )}

      <ConfirmDialog
        open={writeOff !== null}
        title={t('expiryWriteOffTitle')}
        message={writeOff ? t('expiryWriteOffMsg', { qty: writeOff.quantity, product: writeOff.product.name, batch: writeOff.batchNo ?? t('expiryNoBatchNo') }) : ''}
        confirmLabel={t('expiryWriteOff')}
        variant="warning"
        loading={writeOffMutation.isPending}
        onConfirm={() => { if (writeOff) writeOffMutation.mutate(writeOff); }}
        onCancel={() => setWriteOff(null)}
      />
    </div>
  );
}
